import { useEffect } from 'react';

/**
 * After a refused Continue, bring the FIRST field in error into view and put the cursor in it
 * [Operator, 2026-09-07].
 *
 * **The problem this solves is not decoration.** The application form is taller than any kiosk screen — a
 * candidate who leaves three fields blank presses Continue, nothing appears to happen, and the messages that
 * did appear are above or below the fold. On a device with no scrollbar chrome and no keyboard, "nothing
 * happened" is indistinguishable from "the button is broken", and the candidate is stuck with no way to
 * find out otherwise.
 *
 * **First in DOCUMENT order, which is first in visual order.** It queries `[aria-invalid="true"]` rather
 * than taking a list of field names, so the order can never drift from the order the screen actually draws —
 * a hand-maintained list is one reorder away from scrolling to the second problem instead of the first.
 * That also means every control this repo marks invalid is covered for free, including the two Yes/No
 * groups and the consent box, which are not `Field`s.
 *
 * **Focus follows the scroll**, because moving the viewport alone tells a screen-reader user nothing. It is
 * `preventScroll` so the two do not fight over the final position: the smooth scroll wins and the focus ring
 * lands where it stopped.
 *
 * `attempt` is a counter, not a boolean, and that is deliberate — pressing Continue twice with the same
 * errors must scroll twice. A boolean would latch on the first press and silently do nothing after.
 */
export function useScrollToFirstError(attempt: number): void {
  useEffect(() => {
    if (attempt === 0) return;

    const first = document.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (first === null) return;

    // Somebody who has asked their OS to reduce motion gets the jump, not the glide. The rule is the
    // convention skill's; the reason it matters more here is that this scroll can cross a whole screen.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

    // jsdom implements neither method. Guarded rather than mocked in every test that renders a form: a
    // missing browser API must not turn a validation test into a crash.
    first.scrollIntoView?.({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    first.focus?.({ preventScroll: true });
  }, [attempt]);
}
